import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { trackPageView } from '@/lib/analytics'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { AuthProvider, useAuth } from '@/auth/AuthProvider'
import { DiaryProvider } from '@/diary/DiaryProvider'
import { AppShell } from '@/components/AppShell'
import { FullScreenLoader } from '@/components/FullScreenLoader'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SignIn } from '@/routes/SignIn'
import { Gate } from '@/routes/Gate'
import { saveReturnPath } from '@/lib/returnPath'

// Code-split the authenticated app so the sign-in/gate load fast.
const Diary = lazy(() => import('@/routes/Diary').then((m) => ({ default: m.Diary })))
const Log = lazy(() => import('@/routes/Log').then((m) => ({ default: m.Log })))
const SavedFoods = lazy(() => import('@/routes/SavedFoods').then((m) => ({ default: m.SavedFoods })))
const ShoppingList = lazy(() => import('@/routes/ShoppingList').then((m) => ({ default: m.ShoppingList })))
const DiarySearch = lazy(() => import('@/routes/DiarySearch').then((m) => ({ default: m.DiarySearch })))
const MenuResults = lazy(() => import('@/routes/MenuResults').then((m) => ({ default: m.MenuResults })))
const Recipes = lazy(() => import('@/routes/Recipes').then((m) => ({ default: m.Recipes })))
const RecipeDetail = lazy(() => import('@/routes/RecipeDetail').then((m) => ({ default: m.RecipeDetail })))
const RecipeEditor = lazy(() => import('@/routes/RecipeEditor').then((m) => ({ default: m.RecipeEditor })))
const Progress = lazy(() => import('@/routes/Progress').then((m) => ({ default: m.Progress })))
const History = lazy(() => import('@/routes/History').then((m) => ({ default: m.History })))
const Report = lazy(() => import('@/routes/Report').then((m) => ({ default: m.Report })))
const Labs = lazy(() => import('@/routes/Labs').then((m) => ({ default: m.Labs })))
const Settings = lazy(() => import('@/routes/Settings').then((m) => ({ default: m.Settings })))
const SharedRecipe = lazy(() => import('@/routes/SharedRecipe').then((m) => ({ default: m.SharedRecipe })))
// 4.47: "Plan my day" (the app's 4.42 day planner) — Pro-only like the whole web app.
const DayPlan = lazy(() => import('@/routes/DayPlan').then((m) => ({ default: m.DayPlan })))

/** Public routes (sign-in): bounce already-authed users into the app. */
export function PublicOnly() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (user) {
    // Return to the deep link the user originally requested (audit #9).
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from || '/diary'} replace />
  }
  return <Outlet />
}

/** Authed but not necessarily premium — the upgrade gate lives here. */
function RequireAuth() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  return <Outlet />
}

/** Premium/trial only — the whole app. Non-premium users go to the gate. */
export function RequirePremium() {
  const { user, loading, access, profileMissing } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  // No kpal_users profile → send to the gate, which shows the finish-in-app
  // screen (never the app shell, even if some entitlement exists).
  // 4.40 RS: a shared-recipe link (/r/{code}) survives the checkout round trip —
  // the Gate returns to it once access is granted (lib/returnPath; share paths only).
  if (profileMissing) {
    saveReturnPath(location.pathname)
    return <Navigate to="/upgrade" replace />
  }
  if (access === 'checking') return <FullScreenLoader label="Checking your subscription…" />
  if (access === 'denied') {
    saveReturnPath(location.pathname)
    return <Navigate to="/upgrade" replace />
  }
  return (
    <DiaryProvider>
      <AppShell>
        <Suspense fallback={<FullScreenLoader />}>
          <Outlet />
        </Suspense>
      </AppShell>
    </DiaryProvider>
  )
}

// GA4 SPA page views — one per route change.
function PageViewTracker() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname)
  }, [location.pathname])
  return null
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <PageViewTracker />
            <Routes>
              <Route element={<PublicOnly />}>
                <Route path="/sign-in" element={<SignIn />} />
              </Route>

              <Route element={<RequireAuth />}>
                <Route path="/upgrade" element={<Gate />} />
              </Route>

              <Route element={<RequirePremium />}>
                <Route path="/diary" element={<Diary />} />
                <Route path="/log" element={<Log />} />
                <Route path="/saved-foods" element={<SavedFoods />} />
                <Route path="/shopping-list" element={<ShoppingList />} />
                <Route path="/search" element={<DiarySearch />} />
                <Route path="/menu/:scanId" element={<MenuResults />} />
                <Route path="/recipes" element={<Recipes />} />
                <Route path="/recipes/new" element={<RecipeEditor />} />
                <Route path="/recipes/:recipeId" element={<RecipeDetail />} />
                <Route path="/recipes/:recipeId/edit" element={<RecipeEditor />} />
                <Route path="/progress" element={<Progress />} />
                <Route path="/history" element={<History />} />
                <Route path="/report" element={<Report />} />
                <Route path="/labs" element={<Labs />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/plan" element={<DayPlan />} />
                {/* 4.40 RS: shared recipe preview — Pro-only like the whole web app. */}
                <Route path="/r/:code" element={<SharedRecipe />} />
              </Route>

              <Route path="/" element={<Navigate to="/diary" replace />} />
              <Route path="*" element={<Navigate to="/diary" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
